import { cn } from "@/utils/ui";
import { type VariantProps, cva } from "class-variance-authority";
import type * as React from "react";

const inputVariants = cva(
  [
    // Basic layout
    "block w-full appearance-none px-3 py-3",
    // Typography
    "text-gray-fg-primary placeholder:text-gray-fg-secondary",
    // DIsabled
    "disabled:opacity-50 disabled:cursor-not-allowed",
    // Focus
    "focus:outline-hidden",
    // Invalid
    "aria-invalid:border-destructive-border aria-invalid:hover:border-destructive-border-hover aria-invalid:focus-visible:border-destructive-border-active",
    // System
    "dark:[color-schema:dark]",
    // Elements
    "peer-data-[side=start]:ps-12 peer-data-[side=end]:pe-12",
  ],
  {
    variants: {
      variant: {
        surface:
          "bg-gray-ui border border-gray-border hover:border-gray-border-hover focus-visible:border-gray-border-hover",
        soft: "bg-gray-ui hover:bg-gray-ui-hover focus-visible:bg-gray-ui-active",
      },
    },
    defaultVariants: {
      variant: "surface",
    },
  },
);

function Input({
  className,
  type,
  variant,
  ...props
}: React.ComponentProps<"input"> & VariantProps<typeof inputVariants>) {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(inputVariants({ variant }), className)}
      {...props}
    />
  );
}

function InputElement({
  className,
  side = "start",
  ...props
}: React.ComponentProps<"div"> & { side?: "start" | "end" }) {
  return (
    <div
      data-side={side}
      className={cn(
        "peer absolute inset-y-0 flex items-center z-10",
        side === "end" ? "end-0 pe-3.5" : "start-0 ps-3.5",
        className,
      )}
      {...props}
    />
  );
}

export { Input, InputElement };
